import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { getUsers, getEngagementSummary } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsersLast30Days: 0,
    averageLoginsPerUser: 0,
  });
  const [sectionAccess, setSectionAccess] = useState([]);
  const [sectionBreakdown, setSectionBreakdown] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [showEngagementDashboard, setShowEngagementDashboard] = useState(false);
  const [recentActivitySummary, setRecentActivitySummary] = useState({
    totalEvents: 0,
    uniqueUsers: 0,
    latestSection: null,
    latestActivityAt: null,
  });

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        const [users, summary] = await Promise.all([
          getUsers(),
          getEngagementSummary(),
        ]);

        setStats({
          totalUsers: users.length,
          activeUsersLast30Days: summary.activeUsersLast30Days || 0,
          averageLoginsPerUser: summary.averageLoginsPerUser || 0,
        });
        setSectionAccess(summary.sectionAccess || []);
        setSectionBreakdown(summary.sectionBreakdown || []);
        setTopUsers(summary.topUsers || []);
        setRecentActivity(summary.recentActivity || []);
        setRecentActivitySummary(summary.recentActivitySummary || {});
      } catch (err) {
        console.error('Error fetching admin data:', err.message);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, [getUsers, getEngagementSummary]);

  const selectedSectionData = sectionBreakdown.find((item) => item.section === selectedSection) || null;
  const sectionTotalAccesses = sectionAccess.reduce((total, item) => total + item.count, 0);
  const sectionUniqueUsers = new Set(
    sectionBreakdown.flatMap((section) => section.users.map((user) => user.email || 'anonymous'))
  ).size;
  const topUsersTotalLogins = topUsers.reduce((total, user) => total + user.loginCount, 0);
  const leadingUser = topUsers[0];
  const leadingUserLabel = leadingUser?.firstName || leadingUser?.email || 'N/A';

  if (loading) return <div className="admin-dashboard"><p>Loading dashboard data...</p></div>;
  if (error) return <div className="admin-dashboard"><p className="error-text">{error}</p></div>;

  return (
    <div className="admin-dashboard">
      {selectedSectionData && (
        <div className="section-detail-modal" onClick={() => setSelectedSection(null)}>
          <div className="section-detail-modal-content" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedSectionData.section}</h3>
              <button type="button" onClick={() => setSelectedSection(null)}>Close</button>
            </div>
            <p>Total accesses: <strong>{selectedSectionData.count}</strong></p>
            <ul className="modal-user-list">
              {selectedSectionData.users.map((user) => (
                <li key={`${selectedSectionData.section}-${user.email || user.firstName || 'anonymous'}`}>
                  <div>
                    <strong>{user.email || user.firstName || 'Unknown user'}</strong>
                    <small>{user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : 'No name recorded'}</small>
                  </div>
                  <span>{user.accessCount} views</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      <div className="admin-card">
        <h3>Quick Actions</h3>
        <div className="admin-quick-actions">
          <Link to="/register" className="admin-action-btn">
            <i className="fas fa-user-plus"></i>
            <span>Add User</span>
          </Link>

          <Link to="/edit-users" className="admin-action-btn">
            <i className="fas fa-users"></i>
            <span>Edit Users</span>
          </Link>

          <Link to="/admin/citizens" className="admin-action-btn">
            <i className="fas fa-id-card"></i>
            <span>Citizen Registry</span>
          </Link>

          <Link to="/file-manager" className="admin-action-btn">
            <i className="fas fa-file-alt"></i>
            <span>File Manager</span>
          </Link>

          <Link to="/admin/feature-toggles" className="admin-action-btn">
            <i className="fas fa-toggle-on"></i>
            <span>Feature Toggles</span>
          </Link>

          <Link to="/dev-notes" className="admin-action-btn">
            <i className="fas fa-code"></i>
            <span>Dev Notes</span>
          </Link>

          <Link to="/admin/connector-settings" className="admin-action-btn">
            <i className="fas fa-plug"></i>
            <span>Integrations</span>
          </Link>

          <Link to="/admin/integration-hub" className="admin-action-btn">
            <i className="fas fa-code-branch"></i>
            <span>Integration Hub</span>
          </Link>
        </div>
      </div>

      <div className="admin-card">
        <h3>Community Management</h3>
        <div className="admin-community-actions">
          <Link to="/community-news" className="admin-action-btn community-news">
            <i className="fas fa-newspaper"></i>
            <span>Manage Community News</span>
          </Link>

          <Link to="/community-pages" className="admin-action-btn community-pages">
            <i className="fas fa-file-alt"></i>
            <span>Manage Community Pages</span>
          </Link>
        </div>
      </div>

      <div className="engagement-dashboard-header">
        <h2 className="dashboard-title">Community Engagement Dashboard</h2>
        <button
          type="button"
          className="engagement-toggle"
          onClick={() => setShowEngagementDashboard((isVisible) => !isVisible)}
          aria-expanded={showEngagementDashboard}
          aria-controls="community-engagement-content"
        >
          <span aria-hidden="true">{showEngagementDashboard ? '−' : '+'}</span>
          <span>{showEngagementDashboard ? 'Collapse' : 'Expand'}</span>
        </button>
      </div>

      {showEngagementDashboard && <div id="community-engagement-content">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-content">
              <h3>Total Users</h3>
              <p className="stat-value">{stats.totalUsers}</p>
            </div>
            <span className="stat-icon">👥</span>
          </div>

          <div className="stat-card">
            <div className="stat-content">
              <h3>Active in 30 Days</h3>
              <p className="stat-value">{stats.activeUsersLast30Days}</p>
            </div>
            <span className="stat-icon">📈</span>
          </div>

          <div className="stat-card">
            <div className="stat-content">
              <h3>Avg. Logins / User</h3>
              <p className="stat-value">{stats.averageLoginsPerUser.toFixed(1)}</p>
            </div>
            <span className="stat-icon">🔐</span>
          </div>

          <div className="stat-card">
            <div className="stat-content">
              <h3>Top Accessed</h3>
              <p className="stat-value">{sectionAccess[0]?.section || 'N/A'}</p>
            </div>
            <span className="stat-icon">📊</span>
          </div>
        </div>

        <div className="admin-panels">
          <div className="admin-panel">
            <h2>Section Access</h2>
            <div className="panel-content">
              <div className="panel-rollup" aria-label="Section access roll-up">
                <div>
                  <strong>{sectionAccess.length}</strong>
                  <span>sections visited</span>
                </div>
                <div>
                  <strong>{sectionTotalAccesses}</strong>
                  <span>total accesses</span>
                </div>
                <div>
                  <strong>{sectionUniqueUsers}</strong>
                  <span>unique users</span>
                </div>
              </div>
              {sectionAccess.length > 0 ? (
                <ul className="analytics-list">
                  {sectionAccess.map((item) => (
                    <li key={item.section} className="analytics-clickable" onClick={() => setSelectedSection(item.section)}>
                      <span>{item.section}</span>
                      <strong>{item.count}</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No section activity recorded yet.</p>
              )}
            </div>
          </div>

          <div className="admin-panel">
            <h2>Top Users</h2>
            <div className="panel-content">
              <div className="panel-rollup" aria-label="Top users roll-up">
                <div>
                  <strong>{topUsers.length}</strong>
                  <span>users ranked</span>
                </div>
                <div>
                  <strong>{topUsersTotalLogins}</strong>
                  <span>combined logins</span>
                </div>
                <div>
                  <strong>{leadingUserLabel}</strong>
                  <span>leading user</span>
                </div>
              </div>
              {topUsers.length > 0 ? (
                <ul className="analytics-list">
                  {topUsers.map((user) => (
                    <li key={`${user.email}-${user.loginCount}`}>
                      <span>{user.firstName || user.email}</span>
                      <strong>{user.loginCount} logins</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No login activity recorded yet.</p>
              )}
            </div>
          </div>
        </div>

        <div className="recent-activity">
          <h2>Recent Engagement Activity</h2>
          <div className="activity-rollup" aria-label="Recent engagement roll-up">
            <div>
              <strong>{recentActivitySummary.totalEvents || 0}</strong>
              <span>events tracked</span>
            </div>
            <div>
              <strong>{recentActivitySummary.uniqueUsers || 0}</strong>
              <span>users active</span>
            </div>
            <div>
              <strong>{recentActivitySummary.latestSection || 'N/A'}</strong>
              <span>latest section</span>
            </div>
          </div>
          {recentActivity.length > 0 ? (
            <div className="activity-list">
              {recentActivity.map((item) => (
                <div className="activity-item" key={item.id || `${item.section}-${item.createdAt}`}>
                  <div className="activity-dot" aria-hidden="true" />
                  <div>
                    <p><strong>{item.userEmail}</strong> accessed <strong>{item.section}</strong></p>
                    <small>{new Date(item.createdAt).toLocaleString()}</small>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No recent activity recorded.</p>
          )}
        </div>
      </div>}
    </div>
  );
};

export default AdminDashboard;
